import React, { useEffect, useState } from "react";
import { ArrowRight, Phone, Star, MapPin, Shield } from "lucide-react";

const HERO_IMAGES = [
  {
    src: "/hero/porsche.jpg",
    alt: "Porsche 911 Turbo in espresso brown after a ceramic detail",
  },
  {
    src: "/hero/mercedes.jpg",
    alt: "Mercedes-AMG G63 in matte bronze after a full exterior detail",
  },
];

export default function Hero() {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(
      () => setIdx((i) => (i + 1) % HERO_IMAGES.length),
      6500,
    );
    return () => clearInterval(t);
  }, []);

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative w-full overflow-hidden bg-[color:var(--primary)]"
    >
      {/* Rotating full-bleed background images */}
      <div className="absolute inset-0">
        {HERO_IMAGES.map((img, i) => (
          <img
            key={img.src}
            src={img.src}
            alt={img.alt}
            className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-[1800ms] ease-in-out ${
              i === idx ? "opacity-100" : "opacity-0"
            }`}
            loading={i === 0 ? "eager" : "lazy"}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20" />
      </div>

      {/* Content */}
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 pt-32 md:pt-40 pb-24 md:pb-20 flex flex-col gap-6 lg:grid lg:grid-cols-12 min-h-[100svh]">
        <div className="col-span-12 lg:col-span-9 flex flex-col justify-center">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-10 h-px bg-[color:var(--accent)]" />
            <span className="overline text-[color:var(--accent-soft)]">
              Premium Mobile Detailing · Australia
            </span>
          </div>

          <h1
            data-testid="hero-headline"
            className="font-serif font-light tracking-tight text-white text-[clamp(2.75rem,8vw,7rem)] leading-[0.95]"
          >
            Showroom shine.
            <br />
            <span className="italic text-white/80">Delivered to</span>{" "}
            <span className="italic font-medium" style={{ color: "#d2b78a" }}>
              your driveway.
            </span>
          </h1>

          <p className="mt-8 max-w-xl text-white/70 text-base md:text-lg leading-relaxed font-light">
            Precision interior and exterior detailing performed on-site using
            professional-grade products. We come to you — home, office, or
            anywhere in between.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <a
              href="#book"
              data-testid="hero-book-cta"
              className="btn-primary"
              style={{
                background: "#a37434",
                borderColor: "#a37434",
                color: "#fff",
              }}
            >
              Book Your Detail <ArrowRight size={16} />
            </a>
            <a
              href="tel:0450539879"
              data-testid="hero-call-cta"
              className="inline-flex items-center justify-center gap-2 px-6 py-[0.95rem] font-semibold text-white border-2 border-white/80 bg-black/40 hover:bg-white hover:text-neutral-900 transition-colors duration-200"
            >
              <Phone size={16} /> 0450 539 879
            </a>
          </div>
        </div>
      </div>

      {/* Bottom trust bar — flows below content on narrow screens */}
      <div className="relative border-t border-white/10 bg-black/55 backdrop-blur-md">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {[
            { Icon: Star, label: "5★ rated service" },
            { Icon: MapPin, label: "Fully mobile · We come to you" },
            { Icon: Shield, label: "Insured & experienced" },
            { Icon: Phone, label: "0450 539 879" },
          ].map((t) => (
            <div
              key={t.label}
              className="flex items-center gap-3 text-white/85 text-sm"
            >
              <t.Icon size={16} className="text-[color:var(--accent-soft)] shrink-0" />
              <span className="text-xs sm:text-sm">{t.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
